import { Row } from 'react-table';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { RecentCallRecord } from '@frontend/phone-panels';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, TableInstance, useTooltip } from '@frontend/design-system';
import { CALL_INSIGHTS_FF } from '../../constants';
import { trackingId } from '../../pendo-tracking';
import { useShowCallDetailsPanel } from './recent-calls';

export const ViewCallDetailsColumn = ({
  row,
  tableInstance,
  hasAccessToRecordings,
  hoverLabel,
  queryKey,
  isEdited = false,
  registerClick,
}: {
  row: Row<RecentCallRecord>;
  tableInstance: TableInstance<RecentCallRecord>;
  hasAccessToRecordings: boolean;
  hoverLabel?: string;
  queryKey: string[];
  isEdited?: boolean;
  registerClick: () => void;
}) => {
  const { t } = useTranslation('calls');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'bottom',
  });

  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isCallInsightEnabled = getFeatureFlagValue(CALL_INSIGHTS_FF);

  const { open: openCallInsightPanel } = useCallInsightController({
    source: 'callHistory',
  });

  const showPanel = useShowCallDetailsPanel({
    row,
    tableInstance,
    hasAccessToRecordings,
    queryKey,
    sectionFocus: 'details',
  });

  return (
    <Button
      variant='secondary'
      css={{
        padding: theme.spacing(0.5),
        border: 'none',
      }}
      aria-label={t('View call details')}
      trackingId={trackingId({ component: 'recentCalls', context: 'table::panel-trigger' })}
      onClick={() => {
        isCallInsightEnabled ? openCallInsightPanel({ hydratedCall: row.original }) : showPanel();
        // Register custom event to reset call details panel
        document.getElementById('call-details-panel')?.dispatchEvent(new Event('reset-panel'));
        registerClick();
      }}
      {...triggerProps}
    >
      {hoverLabel && <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>}
      {isEdited ? (
        <Icon name='document-sent' color='success' size={24} />
      ) : (
        <Icon name='document-filled' size={24} color={row.isSelected ? 'primary' : 'light'} />
      )}
    </Button>
  );
};
